import { DataGridPro } from "@mui/x-data-grid-pro";
import { useCallback, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { enUS, esES } from "@mui/x-data-grid/locales";
import { dataGridStyles, getRowId, StyledBox } from "../utils";
import { useBoundStore } from "useStore";
import ThumbnailField from "./ThumbnailField";
import useProductMasterData from "../Queries/productMasterDataQuery";
import ChannelTag from "./ChannelTags";
import useAuth from "hooks/useAuth";
import Toolbar from "../Toolbar/Toolbar";
import { ProductMasterSkeletons } from "./ProductMasterSkeletons";
import useCurrentMasterCountry from "views/Components/Shared/hooks/useCurrentMasterCountry";
import useChannelsProductMasterData from "../Queries/ProductMasterChannelsQuery";
import ErrorPage from "views/pages/maintenance/ErrorPage";
import SubtypeField from "./SubtypeField";
import SubStatusField from "./SubStatusField";
import LifeCycleField from "./LifeCycleField";

export default function ProductMasterTable({ handleSentData, loading }) {
  const { data, isLoading, isError, isPending } = useProductMasterData();
  const { user } = useAuth();
  const isAdmin = user.permissions.includes("ProductViewManage:Admin");
  const isViewer = user.permissions.includes("ProductViewManage:View");
  const mainCountry = useCurrentMasterCountry();
  const { data: channels } = useChannelsProductMasterData();

  const mainCountryId = mainCountry?.id;

  const filteredChannel = useMemo(() => {
    return (
      channels?.filter((channel) => channel.country_id === mainCountryId) ?? []
    );
  }, [channels, mainCountryId]);

  const channelKeys = useMemo(() => {
    return filteredChannel.map((channel) => channel.marketplace_sname);
  }, [filteredChannel]);

  const localChannels = useBoundStore((state) => state.localChannels);

  const setLocalChannels = useBoundStore((state) => state.setLocalChannels);

  const paginationModel = useBoundStore(
    (state) => state.productMasterPaginationModel
  );
  const setPaginationModel = useBoundStore(
    (state) => state.setProductMasterPaginationModel
  );

  const selectedColumns = useBoundStore((state) => state.productMasterColumns);

  const CHANNELS = {
    AMZN: "Amazon",
    MELI: "Mercado Libre",
    OFF: "OFF",
    VTEX: "VTEX",
  };

  const filteredRows = useMemo(() => data?.data ?? [], [data]);

  const dataMap = useMemo(() => {
    const items = data?.data;
    if (!Array.isArray(items)) return new Map();
    return new Map(items.map((item) => [item.sku, item]));
  }, [data]);

  const toggleChannel = useCallback(
    (channelToToggle, sku) => {
      const row = dataMap.get(sku);
      const originalChannelsTrue = channelKeys.filter(
        (key) => row?.[key] === true
      );
      const currentChannels = localChannels[sku] ?? originalChannelsTrue;
      let updatedChannels;

      if (currentChannels.includes(channelToToggle)) {
        updatedChannels = currentChannels.filter(
          (ch) => ch !== channelToToggle
        );
      } else {
        updatedChannels = [...currentChannels, channelToToggle];
      }
      setLocalChannels({
        ...localChannels,
        [sku]: updatedChannels,
      });
    },
    [localChannels, setLocalChannels, dataMap, channelKeys]
  );

  const toggleAllChannelsForSku = useCallback(
    (sku) => {
      const row = dataMap.get(sku);
      if (!row) return;

      const originalChannelsTrue = channelKeys.filter(
        (key) => row[key] === true
      );
      const currentChannels = localChannels[sku] ?? originalChannelsTrue;

      // Verifica si todos los canales de la fila actual están seleccionados
      const areAllSelected = currentChannels.length === channelKeys.length;

      let updatedChannels;
      if (areAllSelected) {
        // Si están todos, deselecciona todos
        updatedChannels = [];
      } else {
        // Si no, selecciona todos los canales disponibles
        updatedChannels = [...channelKeys];
      }

      setLocalChannels({
        ...localChannels,
        [sku]: updatedChannels,
      });
    },
    [localChannels, setLocalChannels, dataMap, channelKeys]
  );

  const configString = localStorage.getItem("berry-config");
  const locale = configString ? JSON.parse(configString).locale : "en";

  const columns = useMemo(() => {
    const commonRenderCell = (params) => {
      const sku = params.row.sku;
      const editedChannels = localChannels[sku];
      const isLocallyEdited = localChannels.hasOwnProperty(sku);
      const originalChannelsTrue = channelKeys.filter(
        (key) => params.row[key] === true
      );

      const isRowChannelsAllSelected =
        (isLocallyEdited
          ? editedChannels?.length
          : originalChannelsTrue.length) === channelKeys.length;

      return (
        <Box
          flexWrap="wrap"
          gap={1}
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {/* ChannelTag para "Todos" */}
          <ChannelTag
            key="all"
            title="Todos"
            onToggle={() => toggleAllChannelsForSku(sku)}
            isChecked={isRowChannelsAllSelected}
            isDisabled={!isAdmin}
          />

          {/* Channel Tag para canales */}
          {channelKeys.length > 0 ? (
            channelKeys.map((channel) => {
              const isChecked = isLocallyEdited
                ? editedChannels.includes(channel)
                : originalChannelsTrue.includes(channel);

              return (
                <ChannelTag
                  key={channel}
                  title={
                    channel === "AMZN"
                      ? "AMZ"
                      : channel === "OFF"
                      ? "B2B"
                      : channel === "MELI"
                      ? "Mercado Libre"
                      : filteredChannel.find(
                          (ch) => ch.marketplace_sname === channel
                        )?.marketplace_name || channel
                  }
                  onToggle={() => toggleChannel(channel, sku)}
                  isChecked={isChecked}
                  isDisabled={!isAdmin}
                />
              );
            })
          ) : (
            <em style={{ opacity: 0.5 }}>Sin canales seleccionados</em>
          )}
        </Box>
      );
    };
    return [
      {
        field: "brand",
        headerName: "Brand",
        headerClassName:
          "header border-top border-right custom-header border-left",
        flex: 0.8,
        minWidth: 120,
        filterable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            alignContent={"center"}
            height={"100%"}
          >
            <Typography sx={{ marginLeft: "12px", textTransform: "uppercase" }}>
              {params.value === "Gen" ? "generico" : params?.value || "N/A"}
            </Typography>
          </Stack>
        ),
        sortable: false,
        resizable: false,
        headerAlign: "start",
        cellClassName: "border-left",
        align: "left",
      },
      {
        field: "kam",
        headerName: "KAM",
        headerClassName:
          "header border-top border-right custom-header border-left",
        minWidth: 120,
        filterable: false,
        sortable: false,
        resizable: false,
        flex: 0.8,
        headerAlign: "start",
        align: "left",
        renderCell: (params) => {
          return (
            <Stack
              direction="row"
              alignItems="center"
              alignContent={"center"}
              height={"100%"}
            >
              <Typography sx={{ marginLeft: "12px" }}>
                {params?.value || "N/A"}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "sku",
        headerName: "SKU",
        headerClassName:
          "header  border-top border-right border-left custom-header",
        minWidth: 300,
        filterable: false,
        sortable: false,
        resizable: false,
        flex: 1.5,
        headerAlign: "start",

        renderCell: (params) => {
          return (
            <Box
              flexWrap="wrap"
              gap={2}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                marginLeft: "12px",
              }}
            >
              <ThumbnailField imageUrl={params?.picture_url} />
              <Typography>{params?.value}</Typography>
            </Box>
          );
        },
        align: "left",
        valueGetter: (value, row) => row.sku,
      },
      {
        field: "subtype",
        headerName: "Subtype",
        headerClassName:
          "header  border-top border-right border-left custom-header",
        minWidth: 120,
        filterable: false,
        sortable: false,
        resizable: false,
        flex: 0.8,
        headerAlign: "start",
        align: "left",
        renderCell: (params) => {
          return <SubtypeField value={params?.value} />;
        },
      },
      {
        field: "netsuite_status",
        headerName: "Status",
        headerClassName:
          "header  border-top border-right custom-header border-left",
        flex: 0.8,
        minWidth: 150,
        filterable: false,
        sortable: false,
        resizable: false,
        headerAlign: "start",
        align: "left",
      },
      {
        field: "sub_status",
        headerName: "Sub-status",
        headerClassName:
          "header border-top border-right border-left custom-header",
        minWidth: 120,
        filterable: false,
        sortable: false,
        resizable: false,
        flex: 0.8,
        headerAlign: "start",
        align: "left",
        renderCell: (params) => {
          return <SubStatusField value={params?.value} />;
        },
      },
      {
        field: "life_cycle",
        headerName: "Ciclo de vida",
        headerClassName:
          "header border-top border-right border-left custom-header",
        minWidth: 120,
        filterable: false,
        sortable: false,
        resizable: false,
        flex: 0.8,
        headerAlign: "start",
        align: "left",
        renderCell: (params) => {
          return <LifeCycleField value={params?.value} />;
        },
      },
      {
        field: "stock",
        headerName: "Stock",
        headerClassName:
          "header  border-top border-right custom-header border-left",
        flex: 0.8,
        minWidth: 80,
        filterable: false,
        sortable: false,
        resizable: false,
        headerAlign: "start",
        align: "left",
      },
      {
        field: "channels",
        headerName: "Canales de Venta",
        headerClassName:
          " header border-top custom-header border-left border-right",
        flex: 2.5,
        minWidth: 650,
        filterable: false,
        sortable: false,
        resizable: false,
        headerAlign: "start",
        cellClassName: "border-right",
        align: "left",
        renderCell: commonRenderCell,
      },
    ];
  }, [
    toggleChannel,
    CHANNELS,
    localChannels,
    isAdmin,
    channelKeys,
    filteredChannel,
    toggleAllChannelsForSku,
  ]);

  if (isError) {
    return <ErrorPage />;
  }

  if ((isLoading && !data) || isPending) {
    return <ProductMasterSkeletons />;
  }

  return (
    <Stack
      spacing={2}
      sx={{
        height: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          height: "auto",
          width: "100%",
          borderRadius: "4px",
          background: "#FFF",
        }}
      >
        <Box>
          <Stack gap={2}>
            <Toolbar
              handleSentData={handleSentData}
              loading={loading}
              rows={filteredRows}
              channelKeys={channelKeys}
            />
            <StyledBox>
              <DataGridPro
                rows={filteredRows}
                getRowId={getRowId}
                rowCount={data?.count || 0}
                columns={columns}
                // onRowSelectionModelChange={handleSelectionChange}
                // rowSelectionModel={selectionModel}
                disableSelectionOnClick
                disableRowSelectionOnClick
                disableColumnMenu
                getDetailPanelHeight={() => "auto"}
                pagination
                filterMode="server"
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                disableColumnReorder
                localeText={{
                  ...(locale === "es"
                    ? esES.components.MuiDataGrid.defaultProps.localeText
                    : enUS.components.MuiDataGrid.defaultProps.localeText),
                  noRowsLabel: "No se encontraron resultados",
                }}
                sx={dataGridStyles}
                columnVisibilityModel={selectedColumns}
              />
            </StyledBox>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
